import { FilterKeys } from 'legacy/lut/classes/models/onboarding/FilterKeys'
import {
  ESpecializationMessageCode,
  ESpecializationStatus,
  IMainParam,
} from 'legacy/lut/classes/models/onboarding/IMainParam'
import { OnbPageURLs, RequestTypes } from 'legacy/lut/classes/models/onboarding/Onboarding'
import OnboardingStoreService from 'legacy/lut/classes/services/OnboardingStoreService'
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useEnvironmentCheck } from 'clickNgo/hooks/access'
import { getUrlWithParams } from 'core-components/helpers/url'
import { Button } from 'core-components/library/button'
import { Table } from 'core-components/library/list'
import { Loader } from 'core-components/library/loader'
import { IDataProviderSort, Static } from 'core-components/types'
import { Crud } from 'core/types/Crud'
import { ALLOWED_REQUEST_TYPES } from 'onboarding/components/widgets/public/admin/eligibilityCriteria/constants'
import { useActiveSpecializationCriteriaElements } from 'onboarding/components/widgets/public/admin/eligibilityCriteria/hooks/useActiveSpecializationCriteriaElements'
import { checkIsProgramElementAllowed } from 'onboarding/components/widgets/public/admin/specializations/helpers/checkIsProgramElementAllowed'

import { shouldOpenBpRequestDetail } from '../../../../bpRegistration/helpers/shouldOpenBpRequestDetail'
import { checkIsOnboardingRetailHybrid } from '../../retailOnboarding/helpers/checkIsOnboardingRetailHybrid'

import styles from './SpecializationSelect.module.scss'

interface IProps {
  partnerId: string
  specRequests?: IObjectAny
  spzCode?: string
  partnerSpecs?: IObjectAny[]
  onSelect: (item: {
    Name: string
    SpzCode: string
    MessageCode?: ESpecializationMessageCode
  }) => void
  reqSource?: Crud
  type: RequestTypes
  contactId?: string
  countryISO?: string
}

interface IOpenExistRequestProps {
  requestId: number
  type?: RequestTypes
  spzCode?: string
}

export const SpecializationSelect = ({
  partnerId,
  onSelect,
  type,
  contactId,
  countryISO,
}: IProps) => {
  const [wip, setWip] = useState(true)
  const navigate = useNavigate()
  const { isProd } = useEnvironmentCheck()
  const { composeViewAllCriteriaButton, viewAllCriteriaModalToRender } =
    useActiveSpecializationCriteriaElements()

  const isCriteriaVisible = useMemo(() => ALLOWED_REQUEST_TYPES.includes(type), [type])

  const isProgramElement = checkIsProgramElementAllowed(type)
  const sort = useMemo(() => {
    let result: IDataProviderSort[]
    if (isProgramElement) {
      result = [
        { field: 'PrgElementOrder', dir: 'asc' },
        { field: 'PrgElement', dir: 'asc' },
        { field: 'Order', dir: 'asc' },
      ]
    } else if (type === RequestTypes.BPRegistration) {
      result = [{ field: 'Order', dir: 'asc' }]
    } else {
      result = [{ field: 'EntryId', dir: 'asc' }]
    }

    return result
  }, [type, isProgramElement])

  const source = useMemo(() => {
    return new Crud<IMainParam>({
      fetcher: new Static([] as IMainParam[]),
      keyField: 'code',
    })
  }, [])

  useEffect(() => {
    OnboardingStoreService.fetchMainParameter(type, partnerId, 1, 50, contactId, sort).then(
      (res) => {
        source.clear().then(() => source.create('', res.Data).finally(() => setWip(false)))
      }
    )
  }, [sort, source, type, partnerId, contactId])

  const openExistRequest = ({ requestId, type, spzCode }: IOpenExistRequestProps) => {
    let page = OnbPageURLs.RequestDetail

    if (type === RequestTypes.RetailOnboarding) {
      page = OnbPageURLs.RetailOnboardingDetail
      if (spzCode && checkIsOnboardingRetailHybrid(spzCode)) {
        page = OnbPageURLs.RetailHybridOnboardingDetail
      }
    } else if (type === RequestTypes.BPRegistration) {
      // TEMP (non-prod only): BP_RBI keeps the default request-detail page; other BP
      // requests open the external BP registration form. See shouldOpenBpRequestDetail.
      if (shouldOpenBpRequestDetail(spzCode, isProd)) {
        page = OnbPageURLs.RequestDetail
      } else {
        page = OnbPageURLs.BPRegistration
      }
    }

    navigate(getUrlWithParams({ rid: requestId }, `page/${page}`))
  }

  if (wip) {
    return <Loader />
  }

  return (
    <>
      <Table
        localFiltersKey={FilterKeys.Spacializations}
        className={'splz-select-table'}
        hasExport
        header
        pagination
        source={source}
        columns={[
          isProgramElement
            ? { field: 'programElement', title: 'Program Element' }
            : {
                field: 'code',
                title: 'Specialization Code',
              },
          { field: 'name', title: 'Name' },
          {
            priority: 0,
            field: 'Select',
            title: '',
            hideSettings: true,
            renderFunction: (item: IMainParam) => (
              <div>
                <Button
                  onClick={() => {
                    if (
                      item.request &&
                      (item.status === ESpecializationStatus.wip ||
                        item.status === ESpecializationStatus.contract ||
                        item.status === ESpecializationStatus.contractWip)
                    ) {
                      openExistRequest({
                        requestId: item.request.id,
                        type: item.request.type,
                        spzCode: item.request?.spzCode || item.code,
                      })
                    } else {
                      onSelect({
                        Name: item.name,
                        SpzCode: item.code,
                        MessageCode: item.messageCode,
                      })
                    }
                  }}
                  disabled={
                    item.status === ESpecializationStatus.member ||
                    item.status === ESpecializationStatus.done
                  }
                >
                  {item.status === 'New' ? 'Select' : item.status}
                  {item.request?.spz && (
                    <>
                      <br />
                      {item.request?.spz}
                    </>
                  )}
                </Button>

                {isCriteriaVisible && (
                  <div className={styles.buttonWrapper}>
                    {composeViewAllCriteriaButton(
                      {
                        code: item.code,
                        requestType: type,
                        countryISO: countryISO || '',
                      },
                      { isDisabled: !item.isActiveCriteriaExist }
                    )}
                  </div>
                )}
              </div>
            ),
          },
        ]}
      />

      {isCriteriaVisible && viewAllCriteriaModalToRender}
    </>
  )
}
